/**
 * DRCP Panel
 *
 * Renders the generated Data Review and Cleaning Plan inline within the
 * Study Documents page. Mirrors the EDTSPanel pattern for status, generate
 * / regenerate / export actions, and stale-generation handling.
 *
 * The DRCP is a single Firestore document (no per-section subcollection),
 * so the inline preview reads from drcp.generatedContent and renders each
 * section as a collapsible card.
 */

import { GENERATION_LOCK_STALE_MINUTES, GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { useState, useEffect } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc } from '../../constants/firestorePaths';
import { useToast } from '../../contexts/ToastContext';
import { saveAs } from 'file-saver';
import {
  Loader2,
  Play,
  RefreshCw,
  FileText,
  Download,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  AlertTriangle,
  GitCompareArrows,
  ListChecks,
  Lock,
  ClipboardList,
  Users,
  Database,
  Activity,
  Hash,
  BookOpen,
  Gauge,
  CheckCircle2,
  Layers,
  Tag,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/* ---------- Types ---------- */

interface DrcpDoc {
  version?: number;
  status?: string;
  title?: string;
  generatedAt?: any;
  generatedBy?: string;
  studyInfo?: {
    studyTitle?: string;
    shortTitle?: string;
    protocolNumber?: string;
    sponsor?: string;
    phase?: string;
    therapeuticArea?: string;
    edcSystem?: string;
  };
  generatedContent?: Record<string, any>;
  formCount?: number;
  variableCount?: number;
  ctqCount?: number;
  externalSourceCount?: number;
  kriCount?: number;
  qtlCount?: number;
  _generating?: boolean;
  _generationStartedAt?: any;
  _generationStartedBy?: string;
  _generationType?: string;
  _generationError?: string;
  _generationFailedAt?: any;
}

interface DRCPPanelProps {
  projectId: string;
  /**
   * The Hub's in-flight state for this document, so ONE generation disables
   * EVERY control that can start it.
   *
   * This panel is rendered inside the Hub's drcp card, which carries its
   * own Regenerate button, so two controls for the same action sit on screen
   * together. Each used to hold only its own state. Both do read the server's
   * `_generating` marker, but that is set only after a round trip, so at the
   * instant of a click neither could see the other's — and on 2026-08-17 one
   * click on the MCS card produced two requests 0.67ms apart, the second
   * refused by the per-study lock and shown to the user as a failure while the
   * first was succeeding.
   *
   * The Hub owns the authoritative state (its synchronous `generatingDocs`
   * ORed with the same marker), so passing it down closes that window
   * immediately rather than a round trip later.
   */
  generating?: boolean;
}

/* ---------- Section catalog (drives the inline preview) ---------- */

interface SectionSpec {
  id: string;
  title: string;
  icon: React.ReactNode;
  contentKey: string;
}

const SECTIONS: SectionSpec[] = [
  { id: 'documentControl', title: 'Document Control', icon: <Tag className="h-4 w-4" />, contentKey: 'documentControl' },
  { id: 'overview', title: 'Overview and Scope', icon: <BookOpen className="h-4 w-4" />, contentKey: 'overview' },
  { id: 'studySnapshot', title: 'Study Snapshot', icon: <FileText className="h-4 w-4" />, contentKey: 'studySnapshot' },
  { id: 'rolesAndResponsibilities', title: 'Roles and Responsibilities', icon: <Users className="h-4 w-4" />, contentKey: 'rolesAndResponsibilities' },
  { id: 'dataSources', title: 'Data Sources', icon: <Database className="h-4 w-4" />, contentKey: 'dataSources' },
  { id: 'ctqAndRisk', title: 'CtQ Factors and Risk Assessment', icon: <ShieldCheck className="h-4 w-4" />, contentKey: 'ctqAndRisk' },
  { id: 'reviewStrategy', title: 'Risk-Based Review Strategy', icon: <Activity className="h-4 w-4" />, contentKey: 'reviewStrategy' },
  { id: 'formAndVariableReview', title: 'Form and Variable-Level Review', icon: <Layers className="h-4 w-4" />, contentKey: 'formAndVariableReview' },
  { id: 'queryManagement', title: 'Query Lifecycle and Management', icon: <ListChecks className="h-4 w-4" />, contentKey: 'queryManagement' },
  { id: 'externalDataReconciliation', title: 'External Data Reconciliation', icon: <GitCompareArrows className="h-4 w-4" />, contentKey: 'externalDataReconciliation' },
  { id: 'codingReview', title: 'Medical Coding Review', icon: <Hash className="h-4 w-4" />, contentKey: 'codingReview' },
  { id: 'protocolDeviationsAndMissingData', title: 'Protocol Deviations and Missing Data', icon: <AlertTriangle className="h-4 w-4" />, contentKey: 'protocolDeviationsAndMissingData' },
  { id: 'rbqmKriQtl', title: 'RBQM: KRIs and QTLs', icon: <Gauge className="h-4 w-4" />, contentKey: 'rbqmKriQtl' },
  { id: 'reviewToolsAndOutputs', title: 'Review Tools and Outputs', icon: <ClipboardList className="h-4 w-4" />, contentKey: 'reviewToolsAndOutputs' },
  { id: 'dataCleaningMilestones', title: 'Data Cleaning Milestones', icon: <CheckCircle2 className="h-4 w-4" />, contentKey: 'dataCleaningMilestones' },
  { id: 'databaseLockReadiness', title: 'Database Lock Readiness', icon: <Lock className="h-4 w-4" />, contentKey: 'databaseLockReadiness' },
  { id: 'metricsAndReporting', title: 'Metrics and Reporting', icon: <Gauge className="h-4 w-4" />, contentKey: 'metricsAndReporting' },
  { id: 'trainingAndDocumentControl', title: 'Training and Document Control', icon: <Users className="h-4 w-4" />, contentKey: 'trainingAndDocumentControl' },
];

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  'in-review': 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
};

const PROGRESS_STEPS = [
  'Loading protocol and study setup...',
  'Reading data dictionary and edit checks...',
  'Detecting external data sources...',
  'Identifying critical-to-quality factors...',
  'Authoring risk-based review strategy...',
  'Drafting query lifecycle and SLAs...',
  'Building KRI and QTL catalog...',
  'Defining database lock criteria...',
  'Assembling final document...',
];

/* ---------- Inline preview renderers ---------- */

function PreviewLabel({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-gray-500 font-medium min-w-[140px]">{label}:</span>
      <span className="text-gray-700">{String(value)}</span>
    </div>
  );
}

function PreviewBulletList({ items }: { items: any[] }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="list-disc ml-5 space-y-1 text-xs text-gray-700">
      {items.map((it, i) => (
        <li key={i}>{typeof it === 'string' ? it : JSON.stringify(it)}</li>
      ))}
    </ul>
  );
}

function PreviewTable({ headers, rows }: { headers: string[]; rows: any[][] }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs italic text-gray-400">(no entries)</p>;
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-gray-50">
              {row.map((cell, ci) => (
                <td key={ci} className="px-2 py-1.5 align-top text-gray-700">
                  {Array.isArray(cell) ? cell.join('; ') : (cell ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderPreview(sectionId: string, content: any): React.ReactNode {
  if (!content) {
    return <p className="text-xs italic text-gray-400">No content generated for this section.</p>;
  }
  switch (sectionId) {
    case 'documentControl':
      return (
        <div className="space-y-2">
          <PreviewLabel label="Title" value={content.title} />
          <PreviewLabel label="Version" value={content.documentVersion} />
          {/* Status is the stored workflow status; an effective date and
              approvers are never shown, because older documents carried
              model-written values for both (2026-09-30). */}
          <PreviewLabel label="Status" value={content.status} />
          <PreviewLabel label="Supersedes" value={content.supersedesVersion} />
        </div>
      );

    case 'overview':
      return (
        <div className="space-y-3">
          {content.purpose && <p className="text-xs text-gray-700">{content.purpose}</p>}
          {Array.isArray(content.inScope) && content.inScope.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">In Scope</p>
              <PreviewBulletList items={content.inScope} />
            </div>
          )}
          {Array.isArray(content.outOfScope) && content.outOfScope.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Out of Scope</p>
              <PreviewBulletList items={content.outOfScope} />
            </div>
          )}
          {Array.isArray(content.qualityObjectives) && content.qualityObjectives.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Quality Objectives</p>
              <PreviewBulletList items={content.qualityObjectives} />
            </div>
          )}
          {Array.isArray(content.regulatoryFramework) && content.regulatoryFramework.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Regulatory Framework</p>
              <PreviewBulletList items={content.regulatoryFramework} />
            </div>
          )}
        </div>
      );

    case 'studySnapshot':
      return (
        <div className="space-y-3">
          {content.summary && <p className="text-xs text-gray-700">{content.summary}</p>}
          {Array.isArray(content.keyAttributes) && (
            <div className="space-y-1">
              {content.keyAttributes.map((a: any, i: number) => (
                <PreviewLabel key={i} label={a.label} value={a.value} />
              ))}
            </div>
          )}
        </div>
      );

    case 'rolesAndResponsibilities':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Role', 'RACI', 'Responsibilities']}
            rows={(content.raci || []).map((r: any) => [
              r.role,
              r.raciCode,
              Array.isArray(r.responsibilities) ? r.responsibilities.join('; ') : r.responsibilities,
            ])}
          />
          {content.accountabilityNote && (
            <p className="text-xs text-gray-600 italic mt-2">{content.accountabilityNote}</p>
          )}
        </div>
      );

    case 'dataSources':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          {content.edc && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">EDC System</p>
              <div className="space-y-1">
                <PreviewLabel label="System" value={content.edc.system} />
                <PreviewLabel label="Vendor" value={content.edc.vendor} />
                <PreviewLabel label="Version" value={content.edc.version} />
                <PreviewLabel label="Notes" value={content.edc.notes} />
              </div>
            </div>
          )}
          {Array.isArray(content.externalSources) && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">External Sources</p>
              <PreviewTable
                headers={['Source', 'Vendor', 'Type', 'Frequency', 'Format', 'Reconciliation', 'Intensity']}
                rows={content.externalSources.map((s: any) => [
                  s.name,
                  s.vendor,
                  s.dataType,
                  s.transferFrequency,
                  s.transferFormat,
                  s.reconciliationApproach,
                  s.reviewIntensity,
                ])}
              />
            </div>
          )}
        </div>
      );

    case 'ctqAndRisk':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Critical-to-Quality Factors</p>
            <PreviewTable
              headers={['Factor', 'Category', 'Description', 'Domains', 'Intensity']}
              rows={(content.ctqFactors || []).map((f: any) => [
                f.name,
                f.category,
                f.description,
                Array.isArray(f.affectedDomains) ? f.affectedDomains.join(', ') : f.affectedDomains,
                f.reviewIntensity,
              ])}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Risk Assessment</p>
            <PreviewTable
              headers={['Risk', 'Likelihood', 'Impact', 'Mitigation', 'Owner']}
              rows={(content.riskAssessment || []).map((r: any) => [r.risk, r.likelihood, r.impact, r.mitigation, r.owner])}
            />
          </div>
        </div>
      );

    case 'reviewStrategy':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Cadences</p>
            <PreviewTable
              headers={['Type', 'Frequency', 'Scope', 'Owner', 'Output']}
              rows={(content.cadences || []).map((c: any) => [c.type, c.frequency, c.scope, c.owner, c.output])}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Triggered Reviews</p>
            <PreviewTable
              headers={['Trigger', 'Action', 'Owner']}
              rows={(content.triggers || []).map((t: any) => [t.trigger, t.action, t.owner])}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Approaches</p>
            <PreviewTable
              headers={['Approach', 'When to Use', 'Tools']}
              rows={(content.approaches || []).map((a: any) => [a.approach, a.whenToUse, a.tools])}
            />
          </div>
        </div>
      );

    case 'formAndVariableReview':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Form', 'Vars', 'Priority', 'Review Type', 'Frequency', 'Critical Variables']}
            rows={(content.forms || []).map((f: any) => [
              f.formName,
              f.variableCount,
              f.priority,
              f.reviewType,
              f.reviewFrequency,
              Array.isArray(f.criticalVariables) ? f.criticalVariables.join(', ') : f.criticalVariables,
            ])}
          />
        </div>
      );

    case 'queryManagement':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Categories and SLAs</p>
            <PreviewTable
              headers={['Category', 'Source', 'Owner', 'SLA', 'Example']}
              rows={(content.categories || []).map((c: any) => [c.category, c.source, c.owner, c.sla, c.example])}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Aging Targets</p>
            <PreviewTable
              headers={['Stage', 'Target', 'Metric']}
              rows={(content.agingTargets || []).map((a: any) => [a.stage, a.target, a.metric])}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Escalation</p>
            <PreviewTable
              headers={['Trigger', 'Action', 'Owner']}
              rows={(content.escalation || []).map((e: any) => [e.trigger, e.action, e.owner])}
            />
          </div>
          {Array.isArray(content.metrics) && content.metrics.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Metrics</p>
              <PreviewBulletList items={content.metrics} />
            </div>
          )}
          {content.closureCriteria && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Closure Criteria</p>
              <p className="text-xs text-gray-700">{content.closureCriteria}</p>
            </div>
          )}
        </div>
      );

    case 'externalDataReconciliation':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Vendor', 'Data Type', 'Frequency', 'Keys', 'Categories', 'Routing', 'Sign-off']}
            rows={(content.sources || []).map((s: any) => [
              s.vendor,
              s.dataType,
              s.frequency,
              Array.isArray(s.matchingKeys) ? s.matchingKeys.join(', ') : s.matchingKeys,
              Array.isArray(s.discrepancyCategories) ? s.discrepancyCategories.join(', ') : s.discrepancyCategories,
              s.routing,
              s.finalReconciliationSignoff,
            ])}
          />
        </div>
      );

    case 'codingReview':
      if (content.applicable === false) {
        return <p className="text-xs italic text-gray-500">{content.introduction || 'Not applicable to this study.'}</p>;
      }
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Dictionary', 'Version', 'Applies To']}
            rows={(content.dictionaries || []).map((d: any) => [d.name, d.version, d.appliesTo])}
          />
          <PreviewLabel label="Auto-Coding Policy" value={content.autoCodingPolicy} />
          <PreviewLabel label="QC Sampling" value={content.qcSamplingPolicy} />
          <PreviewLabel label="Upversioning" value={content.upversioningPolicy} />
          <PreviewLabel label="Medical Adjudication" value={content.medicalAdjudication} />
          <PreviewLabel label="Sign-off" value={content.signoffPolicy} />
        </div>
      );

    case 'protocolDeviationsAndMissingData':
      return (
        <div className="space-y-3">
          {content.captureStatement && <p className="text-xs text-gray-700">{content.captureStatement}</p>}
          {content.deviationsIntroduction && <p className="text-xs text-gray-700">{content.deviationsIntroduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Deviation Categories</p>
            <PreviewTable
              headers={['Category', 'Definition', 'Importance']}
              rows={(content.deviationCategories || []).map((c: any) => [c.category, c.definition, c.importanceLevel])}
            />
          </div>
          {Array.isArray(content.deviationDetectionRules) && content.deviationDetectionRules.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Detection Rules</p>
              <PreviewBulletList items={content.deviationDetectionRules} />
            </div>
          )}
          {content.deviationReviewCadence && <PreviewLabel label="Review Cadence" value={content.deviationReviewCadence} />}
          <hr className="border-gray-200" />
          {content.missingDataIntroduction && <p className="text-xs text-gray-700">{content.missingDataIntroduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Missing Data Definitions</p>
            <PreviewTable
              headers={['Code', 'Meaning', 'When Used']}
              rows={(content.missingDefinitions || []).map((d: any) => [d.code, d.meaning, d.whenUsed])}
            />
          </div>
          {content.expectedMissingnessThresholds && (
            <PreviewLabel label="Expected Thresholds" value={content.expectedMissingnessThresholds} />
          )}
          {content.escalationProcedure && (
            <PreviewLabel label="Escalation" value={content.escalationProcedure} />
          )}
        </div>
      );

    case 'rbqmKriQtl':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">KRIs ({(content.kris || []).length})</p>
            <PreviewTable
              headers={['KRI', 'Definition', 'Threshold', 'Action', 'Owner']}
              rows={(content.kris || []).map((k: any) => [k.kri, k.definition, k.threshold, k.action, k.owner])}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">QTLs ({(content.qtls || []).length})</p>
            <PreviewTable
              headers={['Parameter', 'Threshold', 'Rationale', 'Breach Action']}
              rows={(content.qtls || []).map((q: any) => [q.parameter, q.threshold, q.rationale, q.breachAction])}
            />
          </div>
          {content.centralMonitoringNote && (
            <PreviewLabel label="Central Monitoring" value={content.centralMonitoringNote} />
          )}
        </div>
      );

    case 'reviewToolsAndOutputs':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Listings</p>
            <PreviewTable
              headers={['Listing', 'Purpose', 'Frequency', 'Audience']}
              rows={(content.listings || []).map((l: any) => [l.name, l.purpose, l.frequency, l.audience])}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Dashboards</p>
            <PreviewTable
              headers={['Dashboard', 'Purpose', 'Frequency', 'Audience']}
              rows={(content.dashboards || []).map((d: any) => [d.name, d.purpose, d.frequency, d.audience])}
            />
          </div>
        </div>
      );

    case 'dataCleaningMilestones':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Milestone', 'Timing', 'Scope', 'Acceptance Criteria', 'Owner']}
            rows={(content.milestones || []).map((m: any) => [
              m.milestone,
              m.typicalTiming,
              m.scope,
              Array.isArray(m.acceptanceCriteria) ? m.acceptanceCriteria.join('; ') : m.acceptanceCriteria,
              m.owner,
            ])}
          />
        </div>
      );

    case 'databaseLockReadiness':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          {Array.isArray(content.softLockCriteria) && content.softLockCriteria.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Soft Lock Criteria</p>
              <PreviewBulletList items={content.softLockCriteria} />
            </div>
          )}
          {Array.isArray(content.hardLockCriteria) && content.hardLockCriteria.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Hard Lock Criteria</p>
              <PreviewBulletList items={content.hardLockCriteria} />
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Pre-Lock Checklist</p>
            <PreviewTable
              headers={['Item', 'Owner', 'Evidence Required']}
              rows={(content.preLockChecklist || []).map((p: any) => [p.item, p.owner, p.evidenceRequired])}
            />
          </div>
          {content.postLockUnlockPolicy && (
            <PreviewLabel label="Post-Lock Unlock" value={content.postLockUnlockPolicy} />
          )}
        </div>
      );

    case 'metricsAndReporting':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['KPI', 'Definition', 'Target', 'Frequency', 'Audience']}
            rows={(content.kpis || []).map((k: any) => [k.kpi, k.definition, k.target, k.frequency, k.audience])}
          />
          {content.reportingCadence && (
            <PreviewLabel label="Reporting Cadence" value={content.reportingCadence} />
          )}
        </div>
      );

    case 'trainingAndDocumentControl':
      return (
        <div className="space-y-3">
          {Array.isArray(content.trainingRequirements) && content.trainingRequirements.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Training Requirements</p>
              <PreviewBulletList items={content.trainingRequirements} />
            </div>
          )}
          {content.versionControlNote && (
            <PreviewLabel label="Version Control" value={content.versionControlNote} />
          )}
        </div>
      );

    default:
      return <pre className="text-xs text-gray-700 whitespace-pre-wrap">{JSON.stringify(content, null, 2)}</pre>;
  }
}

/* ---------- Component ---------- */

export default function DRCPPanel({ projectId, generating: hubGenerating = false }: DRCPPanelProps) {
  const [drcpDoc, setDrcpDoc] = useState<DrcpDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [exporting, setExporting] = useState(false);
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const { showToast } = useToast();

  const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS;

  const isGeneratingFromMarker = (() => {
    if (!drcpDoc?._generating) return false;
    const startedAt = drcpDoc._generationStartedAt;
    if (!startedAt?.toDate) return true;
    const ageMs = Date.now() - startedAt.toDate().getTime();
    return ageMs < STALE_GENERATION_MS;
  })();

  const isStaleGeneration = (() => {
    if (!drcpDoc?._generating) return false;
    const startedAt = drcpDoc._generationStartedAt;
    if (!startedAt?.toDate) return false;
    return Date.now() - startedAt.toDate().getTime() >= STALE_GENERATION_MS;
  })();

  // Folds in the Hub's in-flight state (the shared guard — see
  // DRCPPanelProps.generating) alongside the local fast path and the Firestore
  // marker, so a click on the card's Regenerate disables this one at once.
  const showGenerating = generating || hubGenerating || (isGeneratingFromMarker && !isStaleGeneration);

  /* --- Firestore subscription --- */
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'drcp'),
      (snapshot) => {
        if (snapshot.exists()) {
          setDrcpDoc(snapshot.data() as DrcpDoc);
        } else {
          setDrcpDoc(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading DRCP:', err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [projectId]);

  /* --- Generate --- */
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(PROGRESS_STEPS[0]);
    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < PROGRESS_STEPS.length) {
        setGenerationProgress(PROGRESS_STEPS[step]);
      }
    }, 12000);

    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_DATA_REVIEW_CLEANING_PLAN_V2, {
        timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE,
      });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('DRCP generation error:', err);
      showToast(err.message || 'Failed to generate Data Review and Cleaning Plan', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  /* --- Export DOCX --- */
  const handleExport = async () => {
    setExporting(true);
    try {
      const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_DRCP_DOCX, {
        timeout: CALLABLE_TIMEOUTS.LONG,
      });
      const result = await exportFn({ projectId });
      const data = result.data as { base64: string; filename: string };
      const byteCharacters = atob(data.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(blob, data.filename);
    } catch (err: any) {
      console.error('DRCP export error:', err);
      showToast(err.message || 'Failed to export DRCP', 'error');
    } finally {
      setExporting(false);
    }
  };

  /* --- Loading --- */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const hasRealContent = !!(drcpDoc && (drcpDoc.version || drcpDoc.title));

  /* --- Not yet generated --- */
  if (!drcpDoc || !hasRealContent) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate a Data Review and Cleaning Plan defining how data will be reviewed,
            queried, reconciled, and cleaned across the lifetime of the study, with
            risk-based review intensities tied to critical-to-quality factors and
            study-specific KRIs and QTLs.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Content is generated from your protocol, study setup, data dictionary, edit checks,
            and detected external data sources, grounded in ICH E6(R3), GCDMP, TransCelerate
            RBM/QTL guidance, and FDA risk-based monitoring.
          </p>

          {drcpDoc?._generationError && !drcpDoc?._generating && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <strong>Previous generation failed:</strong> {displayGenerationError(drcpDoc._generationError)}
            </div>
          )}

          {isStaleGeneration && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
              You can try generating again.
            </div>
          )}

          {showGenerating ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {generating ? 'Generating...' : 'Generation in progress in the background...'}
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                This may take 2-4 minutes for the full document to generate.
              </p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              <Play className="h-4 w-4" />
              Generate Data Review and Cleaning Plan
            </button>
          )}
        </div>
      </div>
    );
  }

  /* --- DRCP exists: full UI --- */
  const status = drcpDoc.status || 'draft';
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const content = drcpDoc.generatedContent || {};

  return (
    <div className="space-y-3">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 flex-wrap gap-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-gray-800">Data Review and Cleaning Plan</h2>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}>
            {status === 'in-review' ? 'In Review' : status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            {drcpDoc.formCount ?? 0} forms
            {' · '}
            {drcpDoc.ctqCount ?? 0} CtQ
            {' · '}
            {drcpDoc.kriCount ?? 0} KRIs
            {' · '}
            {drcpDoc.qtlCount ?? 0} QTLs
            {' · '}
            {drcpDoc.externalSourceCount ?? 0} ext sources
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export as Word
          </button>
        </div>
      </div>

      {/* Previous error */}
      {drcpDoc?._generationError && !drcpDoc?._generating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <strong>Previous generation failed:</strong> {displayGenerationError(drcpDoc._generationError)}
        </div>
      )}

      {isStaleGeneration && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
          You can try generating again.
        </div>
      )}

      {showGenerating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-700 mb-1">
            {generating ? 'Regenerating...' : 'Generation in progress in the background...'}
          </p>
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
          </div>
          <div className="mt-2 max-w-xs">
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
            </div>
          </div>
        </div>
      )}

      {/* Section list */}
      <div className="space-y-2">
        {SECTIONS.map((section, idx) => {
          const sectionContent = content[section.contentKey];
          const isExpanded = expandedSectionId === section.id;
          return (
            <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setExpandedSectionId(isExpanded ? null : section.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                )}
                <span className="text-xs font-mono text-gray-400 w-8">{idx + 1}</span>
                <span className="text-violet-600">{section.icon}</span>
                <span className="text-sm font-medium text-gray-800 flex-1">{section.title}</span>
                {!sectionContent && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    empty
                  </span>
                )}
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 ml-11 max-h-[60vh] overflow-y-auto">
                  {renderPreview(section.id, sectionContent)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Regenerate */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (
              confirm(
                'Regenerate the entire Data Review and Cleaning Plan with the latest study data? Existing content will be overwritten.',
              )
            ) {
              handleGenerate();
            }
          }}
          disabled={showGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${showGenerating ? 'animate-spin' : ''}`} />
          Regenerate
        </button>
      </div>
    </div>
  );
}
